import * as React from 'react';
import { AppProvider, Router, type Session, type Navigation } from '@toolpad/core/AppProvider';
import { DashboardLayout, ThemeSwitcher } from '@toolpad/core/DashboardLayout';
import { PageContainer } from '@toolpad/core/PageContainer';
import { Routes, Route, useLocation, useNavigate, Navigate } from 'react-router-dom';
import { Box, CircularProgress, Container, Typography, keyframes } from '@mui/material';
import { ProtectedRoute } from '../services/common/protected-route';
import { SystemRole } from '../helper-types/general/enums/system-role';
import { SystemClaim } from '../helper-types/general/enums/system-claim';
import LanguageSwitchComponent from './language-switch';
import getNavigation from './navigation';
import { useAuth } from '../services/common/auth-context';
import { useTranslation } from 'react-i18next';
import getTheme from './theme';
import i18n from './i18n';
import { LanguageCode } from '../helper-types/general/consts/language-code';
import { usePWAUpdate } from '../hooks/usePWAUpdate';
import { Account } from '@toolpad/core';
import { Login, Logout } from '@mui/icons-material';

// Lazy-loaded components
const HomePage = React.lazy(() => import('../pages/home/home-page'));
const PatientTablePage = React.lazy(() => import('../pages/patient/patient-table-page'));
const AddPatientFormPage = React.lazy(() => import('../pages/patient/add-patient-form-page'));
const PatientAppointmentQueuePage = React.lazy(() => import('../pages/appointment/patient-appointment-queue-page'));
const PrescriptionTablePage = React.lazy(() => import('../pages/prescription/prescription-table-page'));
const AddPrescriptionFormPage = React.lazy(() => import('../pages/prescription/add-prescription-form-page'));
const UpdateUserProfileFormPage = React.lazy(() => import('../pages/user/update-user-profile-form-page'));
const UserTablePage = React.lazy(() => import('../pages/user/user-table-page'));
const AddUserFormPage = React.lazy(() => import('../pages/user/add-user-from-page'));
const RoleTablePage = React.lazy(() => import('../pages/role/role-table-page'));
const AddRoleFormPage = React.lazy(() => import('../pages/role/add-role-form-page'));
const PhysicianSubscriptionsPage = React.lazy(() => import('../pages/physician/physician-subscriptions-page'));
const LoginPage = React.lazy(() => import('../pages/auth/login/login-page'));
const UnauthorizedPage = React.lazy(() => import('../pages/common/unauthorized-page'));
const NotFoundPage = React.lazy(() => import('../pages/common/not-found-page'));

interface LayoutProps {
    setDirection: React.Dispatch<React.SetStateAction<'ltr' | 'rtl'>>;
}

function ToolbarActions() {
    const { t, i18n } = useTranslation();
    const [isArabic, setIsArabic] = React.useState(() => {
        const savedLanguage = localStorage.getItem('language');
        return savedLanguage === LanguageCode.AR;
    });
    const { setDirection } = React.useContext(LayoutContext);
    const { UpdateDialog } = usePWAUpdate();

    React.useEffect(() => {
        const savedLanguage = localStorage.getItem('language') || LanguageCode.EN;
        i18n.changeLanguage(savedLanguage);
        setDirection(savedLanguage === LanguageCode.AR ? 'rtl' : 'ltr');
        setIsArabic(savedLanguage === LanguageCode.AR);
    }, [i18n, setDirection]);

    const handleLanguageSwitch = () => {
        const newLang = isArabic ? LanguageCode.EN : LanguageCode.AR;
        i18n.changeLanguage(newLang);
        localStorage.setItem('language', newLang);
        setDirection(newLang === LanguageCode.AR ? 'rtl' : 'ltr');
        setIsArabic(!isArabic);
    };

    return (
        <>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2 }}>
                <LanguageSwitchComponent checked={isArabic} onChange={handleLanguageSwitch} />
                <ThemeSwitcher />
            </Box>
            {UpdateDialog && <UpdateDialog />}
        </>
    );
}

function ToolbarAccount() {
    const { t } = useTranslation();
    return (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2 }}>
            <Account localeText={{
                accountSignInLabel: t('auth.signIn'),
                accountSignOutLabel: t('auth.signOut'),
            }}
                slotProps={{
                    signInButton: {
                        startIcon: <Login />,
                        variant: 'contained',
                        title: t('auth.signIn'),
                        sx: {
                            width: 'fit-content',
                        },
                        color: 'info',
                    },
                    signOutButton: {
                        variant: 'contained',
                        title: t('auth.signOut'),
                    },
                }}
            />
        </Box>
    );
}

export const LayoutContext = React.createContext<{
    setDirection: React.Dispatch<React.SetStateAction<'ltr' | 'rtl'>>;
}>({
    setDirection: () => { },
});

export default function Layout({ setDirection }: LayoutProps) {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const { isAuthenticated, userRoles, user, clinicName, logout, isLoading } = useAuth();

    // Check if current route is login page
    const isLoginPage = location.pathname === '/login';

    // App router
    const router: Router = React.useMemo(
        () => ({
            pathname: location.pathname,
            searchParams: new URLSearchParams(location.search),
            navigate: (path: string | URL) => navigate(String(path)),
        }),
        [location.pathname, location.search, navigate]
    );

    // Session based on auth context user
    const session: Session | null = user
        ? {
            user: {
                name: user.name,
                email: user.email,
                image: user.image,
            },
        }
        : null;

    // Authentication handlers
    const authentication = React.useMemo(
        () => ({
            signIn: async () => {
                navigate('/login');
            },
            signOut: async () => {
                logout();
                navigate('/login');
            },
        }),
        [logout, navigate]
    );

    // Branding
    const branding = {
        title: window.innerWidth < 600 ? '' : clinicName || t('home.EyadtiSystem'),
        logo: <img src="/assets/icons/logo.png" alt="logo" />,
    };

    // Dynamic navigation
    const navigation: Navigation = getNavigation(userRoles, isAuthenticated);

    // Theme
    const theme = getTheme(i18n.language === LanguageCode.AR ? 'rtl' : 'ltr');

    // Show splash screen while auth state is being resolved
    if (isLoading) {
        const fadeIn = keyframes`
            from { opacity: 0; transform: scale(0.9); }
            to { opacity: 1; transform: scale(1); }
        `;

        const pulse = keyframes`
            0%, 100% { opacity: 0.6; }
            50% { opacity: 1; }
        `;

        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100vh',
                    width: '100vw',
                    background: 'linear-gradient(145deg, #F8FAFC 0%, #EEF2FF 50%, #F0F9FF 100%)',
                    '[data-toolpad-color-scheme="dark"] &': {
                        background: 'linear-gradient(145deg, #0F172A 0%, #1E1B4B 50%, #0C1222 100%)',
                    },
                    gap: 3,
                }}
            >
                <Box
                    sx={{
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 120,
                        height: 120,
                        animation: `${fadeIn} 0.6s ease-out`,
                    }}
                >
                    <CircularProgress
                        size={120}
                        thickness={1.5}
                        variant="determinate"
                        value={100}
                        sx={{
                            position: 'absolute',
                            color: '#2563EB',
                            opacity: 0.15,
                            '[data-toolpad-color-scheme="dark"] &': {
                                color: '#3B82F6',
                                opacity: 0.2,
                            },
                        }}
                    />
                    <CircularProgress
                        size={120}
                        thickness={1.5}
                        sx={{
                            position: 'absolute',
                            color: '#2563EB',
                            '[data-toolpad-color-scheme="dark"] &': {
                                color: '#3B82F6',
                            },
                        }}
                    />
                    <Box
                        component="img"
                        src="/assets/icons/logo.png"
                        alt="logo"
                        sx={{
                            width: 64,
                            height: 64,
                            objectFit: 'contain',
                            borderRadius: '16px',
                        }}
                    />
                </Box>
                <Typography
                    variant="body2"
                    sx={{
                        fontFamily: 'Cairo, "Inter", "Roboto", "Helvetica", "Arial", sans-serif',
                        color: '#64748B',
                        fontWeight: 500,
                        letterSpacing: '0.05em',
                        animation: `${pulse} 2s ease-in-out infinite`,
                        '[data-toolpad-color-scheme="dark"] &': {
                            color: '#94A3B8',
                        },
                    }}
                >
                    {t('home.EyadtiSystem')}
                </Typography>
            </Box>
        );
    }

    // Redirect unauthenticated users to login before rendering DashboardLayout
    if (!isAuthenticated && !isLoginPage) {
        return <Navigate to="/login" replace />;
    }

    // Render login page without DashboardLayout
    if (isLoginPage) {
        return (
            <LayoutContext.Provider value={{ setDirection }}>
                <AppProvider
                    session={session}
                    authentication={authentication}
                    navigation={navigation}
                    router={router}
                    branding={branding}
                    theme={theme}
                >
                    <Routes>
                        <Route element={<ProtectedRoute isPublic />}>
                            <Route path="/login" element={<LoginPage />} />
                        </Route>
                    </Routes>
                </AppProvider>
            </LayoutContext.Provider>
        );
    }

    return (
        <LayoutContext.Provider value={{ setDirection }}>
            <AppProvider
                session={session}
                authentication={authentication}
                navigation={navigation}
                router={router}
                branding={branding}
                theme={theme}
            >
                <DashboardLayout
                    slots={{
                        toolbarActions: ToolbarActions,
                        toolbarAccount: ToolbarAccount,
                    }}
                >
                    <Container maxWidth="xl" sx={{ padding: 2, marginTop: 2 }}>
                        <Routes>
                            <Route
                                element={
                                    <ProtectedRoute
                                        requiredRoles={[
                                            SystemRole.Administrator.toString(),
                                            SystemRole.Physician.toString(),
                                            SystemClaim.AccessPatientsGrid.toString(),
                                            SystemClaim.AccessAppointmentsGrid.toString(),
                                            SystemClaim.AccessPrescriptionsGrid.toString(),
                                            SystemClaim.AccessUserManagementMenu.toString(),
                                        ]}
                                    />
                                }
                            >
                                <Route path="" element={<HomePage />} />
                            </Route>
                            <Route
                                element={
                                    <ProtectedRoute
                                        requiredRoles={[SystemRole.Administrator.toString(), SystemRole.Physician.toString(), SystemClaim.AccessPatientsGrid.toString()]}
                                    />
                                }
                            >
                                <Route path="/patient-table" element={<PatientTablePage />} />
                                <Route path="/patient/add" element={<AddPatientFormPage />} />
                            </Route>
                            <Route
                                element={
                                    <ProtectedRoute
                                        requiredRoles={[SystemRole.Administrator.toString(), SystemRole.Physician.toString(), SystemClaim.AccessAppointmentsGrid.toString()]}
                                    />
                                }
                            >
                                <Route path="/appointment-queue" element={<PatientAppointmentQueuePage />} />
                            </Route>
                            <Route
                                element={
                                    <ProtectedRoute
                                        requiredRoles={[SystemRole.Administrator.toString(), SystemRole.Physician.toString(), SystemClaim.AccessPrescriptionsGrid.toString()]}
                                    />
                                }
                            >
                                <Route path="/prescription-table" element={<PrescriptionTablePage />} />
                                <Route path="/prescription/add" element={<AddPrescriptionFormPage />} />
                            </Route>
                            <Route
                                element={
                                    <ProtectedRoute
                                        requiredRoles={[
                                            SystemRole.Administrator.toString(),
                                            SystemRole.Physician.toString(),
                                            SystemClaim.AccessPatientsGrid.toString(),
                                            SystemClaim.AccessAppointmentsGrid.toString(),
                                            SystemClaim.AccessPrescriptionsGrid.toString(),
                                            SystemClaim.AccessUserManagementMenu.toString(),
                                        ]}
                                    />
                                }
                            >
                                <Route path="/security/profile" element={<UpdateUserProfileFormPage />} />
                            </Route>
                            <Route
                                element={
                                    <ProtectedRoute
                                        requiredRoles={[SystemRole.Administrator.toString(), SystemRole.Physician.toString(), SystemClaim.AccessUserManagementMenu.toString()]}
                                    />
                                }
                            >
                                <Route path="/security/user-table" element={<UserTablePage />} />
                                <Route path="/security/user/add" element={<AddUserFormPage />} />
                                <Route path="/security/role-table" element={<RoleTablePage />} />
                                <Route path="/security/role/add" element={<AddRoleFormPage />} />
                            </Route>
                            <Route
                                element={
                                    <ProtectedRoute
                                        requiredRoles={[SystemRole.Administrator.toString()]}
                                    />
                                }
                            >
                                <Route path="/security/physician-subscriptions" element={<PhysicianSubscriptionsPage />} />
                            </Route>
                            <Route path="/unauthorized" element={<UnauthorizedPage />} />
                            <Route path="*" element={<NotFoundPage />} />
                        </Routes>
                    </Container>
                </DashboardLayout>
            </AppProvider>
        </LayoutContext.Provider>
    );
}